import type { KeyValue } from '../net/progress';

/** Statut d'une classe dans le menu : jouable, annoncée (Prochainement) ou verrouillée. */
export type ClassStatus = 'playable' | 'soon' | 'locked';

export interface ClassEntry {
  /** Identifiant de jeu (celui de `src/data/classes/<id>.json` quand la classe est jouable) et nom du fichier de portrait. */
  id: string;
  name: string;
  /** Rôle court affiché sous le nom (research/official/classes.json). */
  role: string;
  /** Origine du portrait : client Dofus Retro (buste détouré) ou client Dofus Unity (illustration). */
  art: 'retro' | 'unity';
}

/** Les 18 classes dans l'ordre Dofus (Forgelance exclue). */
export const CLASS_LIST: readonly ClassEntry[] = [
  { id: 'feca', name: 'Féca', role: 'Protecteur', art: 'retro' },
  { id: 'osamodas', name: 'Osamodas', role: 'Invocateur de créatures', art: 'retro' },
  { id: 'enutrof', name: 'Enutrof', role: 'Chasseur de trésors', art: 'retro' },
  { id: 'sram', name: 'Sram', role: 'Assassin', art: 'retro' },
  { id: 'xelor', name: 'Xélor', role: 'Maître du temps', art: 'retro' },
  { id: 'ecaflip', name: 'Ecaflip', role: 'Combattant imprévisible', art: 'retro' },
  { id: 'eniripsa', name: 'Eniripsa', role: 'Guérisseur', art: 'retro' },
  { id: 'iop', name: 'Iop', role: 'Guerrier téméraire', art: 'retro' },
  { id: 'cra', name: 'Crâ', role: 'Archer', art: 'retro' },
  { id: 'sadida', name: 'Sadida', role: 'Sorcier sylvestre', art: 'retro' },
  { id: 'sacrieur', name: 'Sacrieur', role: 'Berserker', art: 'retro' },
  { id: 'pandawa', name: 'Pandawa', role: 'Bagarreur assoiffé', art: 'retro' },
  { id: 'roublard', name: 'Roublard', role: 'Expert en explosifs', art: 'unity' },
  { id: 'zobal', name: 'Zobal', role: 'Guerrier lunatique', art: 'unity' },
  { id: 'steamer', name: 'Steamer', role: 'Technomage', art: 'unity' },
  { id: 'eliotrope', name: 'Eliotrope', role: 'Créateur de portails', art: 'unity' },
  { id: 'huppermage', name: 'Huppermage', role: 'Mage élémentaliste', art: 'unity' },
  { id: 'ouginak', name: 'Ouginak', role: 'Barbare', art: 'unity' },
];

/** Nom affichable de chaque classe (Hub, HUD), indexé par identifiant. */
export const CLASS_NAME: Record<string, string> = Object.fromEntries(CLASS_LIST.map((c) => [c.id, c.name]));

/** Classe par défaut (et de repli) : le Xélor. Les classes dont le contenu existe (Xélor, Sacrieur, Sadida) peuvent démarrer une run. */
export const DEFAULT_CLASS_ID = 'xelor';
const PLAYABLE: ReadonlySet<string> = new Set([DEFAULT_CLASS_ID, 'sacrieur', 'sadida']);
const SOON: ReadonlySet<string> = new Set<string>();

export const CLASS_STORAGE_KEY = 'rogfus.classId';

export function classStatus(id: string): ClassStatus {
  if (PLAYABLE.has(id)) return 'playable';
  if (SOON.has(id)) return 'soon';
  return 'locked';
}

export const STATUS_LABEL: Record<ClassStatus, string> = { playable: 'Jouable', soon: 'Prochainement', locked: 'Verrouillé' };

export const classPortraitUrl = (id: string): string => `/assets/ui/classes/${id}.png`;

export const isKnownClass = (id: unknown): id is string => typeof id === 'string' && CLASS_LIST.some((c) => c.id === id);

/** Une classe peut démarrer une run seulement si elle est jouable. */
export const canStartRun = (id: string): boolean => classStatus(id) === 'playable';

/** Classe réellement utilisée pour une run : la classe demandée si elle est jouable, sinon le Xélor (classe verrouillée ou inconnue). */
export const resolveRunClass = (id: unknown): string => (isKnownClass(id) && canStartRun(id) ? id : DEFAULT_CLASS_ID);

/** Classe mémorisée (`rogfus.classId`), ramenée au Xélor si la valeur est absente, inconnue ou non jouable. */
export function loadChosenClass(kv: KeyValue): string {
  try { return resolveRunClass(kv.get(CLASS_STORAGE_KEY)); } catch { return DEFAULT_CLASS_ID; }
}

/** Mémorise le choix et renvoie la classe retenue (seule une classe jouable est enregistrée). */
export function saveChosenClass(kv: KeyValue, id: string): string {
  const chosen = resolveRunClass(id);
  try { kv.set(CLASS_STORAGE_KEY, chosen); } catch { /* stockage indisponible : le choix reste valable pour cette session */ }
  return chosen;
}

/** Index de la carte voisine dans la grille (flèches) : borné, sans retour à la ligne. */
export function moveInGrid(index: number, key: string, columns: number, count: number): number {
  const cols = Math.max(1, columns);
  let next = index;
  if (key === 'ArrowRight') next = index + 1;
  else if (key === 'ArrowLeft') next = index - 1;
  else if (key === 'ArrowDown') next = index + cols;
  else if (key === 'ArrowUp') next = index - cols;
  else if (key === 'Home') next = 0;
  else if (key === 'End') next = count - 1;
  return next < 0 || next >= count ? index : next;
}
